import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetManagingDirectorsQuery } from "../../../store/appApi";
import { createMdSchdule } from "../../../services/mdschdule";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const INITIAL_FORM = {
  mdId: "",
  scheduleName: "",
  description: "",
  date: new Date(),
  status: "Upcoming",
  remarks: "",
};

const CreateMdSchedule = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: mdData } = useGetManagingDirectorsQuery();
  const managingDirectors = Array.isArray(mdData?.managingDirectors)
    ? mdData.managingDirectors
    : [];

  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.mdId) errs.mdId = "Select a Managing Director.";
    if (!form.scheduleName.trim())
      errs.scheduleName = "Schedule name is required.";
    if (!form.date) errs.date = "Date is required.";
    return errs;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    try {
      setSaving(true);
      await createMdSchdule({
        mdId: form.mdId,
        scheduleName: form.scheduleName.trim(),
        description: form.description,
        date: form.date,
        status: form.status,
        remarks: form.remarks,
      });
      dispatch(appApi.util.invalidateTags(["MdSchedules"]));
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "The schedule entry was created.",
        timer: 1500,
        showConfirmButton: false,
      });
      navigate("/md-schedule");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message ||
          "Could not create the schedule entry.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="MD Schedule" motherMenu="MD Schedule" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
            MD Schedule
          </div>
          <h1 className="mt-4 text-3xl font-semibold text-gray-900">
            Add Schedule Entry
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Add an item to the MD's schedule — status and remarks can be
            updated later from the list.
          </p>
        </section>

        <section className={`${panelClass} p-5 md:p-6`}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className={labelClass}>Managing Director</label>
                <select
                  className={inputClass}
                  autoComplete="off"
                  value={form.mdId}
                  onChange={set("mdId")}
                >
                  <option value="">Select a Managing Director</option>
                  {managingDirectors.map((md) => (
                    <option key={md._id} value={md._id}>
                      {md.name}
                    </option>
                  ))}
                </select>
                {errors.mdId && (
                  <p className="mt-1 text-xs text-red-600">{errors.mdId}</p>
                )}
                {!managingDirectors.length && (
                  <p className="mt-1 text-xs text-gray-500">
                    No staff are flagged as Managing Director yet — set that
                    on the staff's profile first.
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>Schedule Name</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={form.scheduleName}
                  onChange={set("scheduleName")}
                  placeholder="Site visit, board meeting, client call..."
                />
                {errors.scheduleName && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.scheduleName}
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>Date</label>
                <DatePicker
                  selected={form.date}
                  onChange={(date) => setForm((prev) => ({ ...prev, date }))}
                  className={inputClass}
                  autoComplete="off"
                  dateFormat="dd/MM/yyyy"
                />
                {errors.date && (
                  <p className="mt-1 text-xs text-red-600">{errors.date}</p>
                )}
              </div>
            </div>

            <div>
              <label className={labelClass}>Description</label>
              <textarea
                className={inputClass}
                rows={3}
                value={form.description}
                onChange={set("description")}
                placeholder="Details about this schedule item"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Status</label>
                <select
                  className={inputClass}
                  autoComplete="off"
                  value={form.status}
                  onChange={set("status")}
                >
                  <option value="Upcoming">Upcoming</option>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Remarks</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={form.remarks}
                  onChange={set("remarks")}
                  placeholder="Optional note"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                className="rounded-full border border-gray-300 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700"
                onClick={() => navigate("/md-schedule")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving}
              >
                {saving ? "Saving..." : "Create Schedule"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </>
  );
};

export default CreateMdSchedule;
